/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslate } from "react-admin";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {
  Typography,
  Grid,
  Divider,
  Chip,
} from "@material-ui/core";
import ButtonFooter from "components/ButtonFooter";
import { moveGroupToParent } from "services/setting";
import SnackbarUtils from "utils/SnackbarUtils";
import {
  findAccountGroupNode,
  flattenAccountGroupMoveTargets,
  isAccountGroupDescendant,
  isSameAccountGroupId,
} from "utils/accountGroupingHierarchy";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    margin: 12,
  },
  content: {
    padding: 4,
  },
  paper: {
    borderRadius: 16,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  currentCard: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    backgroundColor: "#3563f6",
    color: "#fff",
    marginBottom: theme.spacing(2),
  },
  optionCard: {
    padding: theme.spacing(0.95, 1.1),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    appearance: "none",
    font: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    boxSizing: "border-box",
  },
  optionBody: {
    minWidth: 0,
    flex: 1,
  },
  optionSelected: {
    borderColor: "#5b50f6",
    backgroundColor: "#edf0ff",
  },
  optionCode: {
    color: "#4256d0",
    fontWeight: 700,
    fontSize: "0.84rem",
  },
  optionName: {
    color: "#1f2a44",
    fontSize: "0.92rem",
  },
  rootLabel: {
    fontWeight: 700,
    color: "#1f2a44",
  },
  dialogActions: {
    justifyContent: "flex-end",
    padding: theme.spacing(1, 3, 2),
  },
}));

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();
  return (
    <MuiDialogTitle disableTypography {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const getLevelNumber = (level) => {
  if (!level) return 1;
  if (typeof level === "number" && Number.isFinite(level)) {
    return level;
  }
  if (typeof level === "string") {
    const match = level.match(/\d+/);
    if (match) {
      return Number(match[0]);
    }
  }
  return 1;
};

const getSubtreeDepth = (node) => {
  if (!node?.Children || node.Children.length === 0) {
    return 1;
  }
  return 1 + Math.max(...node.Children.map((child) => getSubtreeDepth(child)));
};

const DialogMoveGroup = (props) => {
  const translate = useTranslate();
  const classes = useStyles();
  const { id, title, open, onClose, currentNode, treeData } = props;
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState("");
  const mountedRef = useRef(true);
  const movingGroupId = currentNode?.AccGroupId ?? currentNode?.Id;
  const currentTreeNode = findAccountGroupNode(treeData, movingGroupId);
  const currentSubtreeDepth = getSubtreeDepth(currentTreeNode);
  const currentLevel = getLevelNumber(currentNode?.Level || 1);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const validParents = flattenAccountGroupMoveTargets(treeData, movingGroupId).filter(
    (node) => {
      const nodeLevel = getLevelNumber(node.Level);
      return (
        !isAccountGroupDescendant(treeData, movingGroupId, node.AccGroupId ?? node.Id) &&
        nodeLevel + currentSubtreeDepth <= 4 &&
        !(node.Account || []).length
      );
    }
  );

  const handleSave = async () => {
    if (isAccountGroupDescendant(treeData, movingGroupId, selectedParentId)) {
      SnackbarUtils.warning(translate("ra.field.Move Group Circular Warning"));
      return;
    }

    const targetParent = validParents.find((item) =>
      isSameAccountGroupId(item.Id, selectedParentId),
    );

    if (!targetParent) {
      SnackbarUtils.warning(translate("ra.field.Move Group Warning"));
      return;
    }

    if (getLevelNumber(targetParent.Level) + currentSubtreeDepth > 4) {
      SnackbarUtils.warning(translate("ra.field.Move Group Warning"));
      return;
    }

    setBtnLoading(true);
    const { Code, UserMessage, InternalMessage } = await moveGroupToParent({
      GroupId: id,
      NewParentGroupId: selectedParentId || null,
    });
    if (Code === 0) {
      SnackbarUtils.success(UserMessage);
      onClose(true);
    } else {
      if (InternalMessage) {
        SnackbarUtils.error(InternalMessage);
      } else {
        SnackbarUtils.warning(UserMessage);
      }
    }
    if (mountedRef.current) {
      setBtnLoading(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  const moveDisabled = currentLevel <= 1;

  return (
    <div>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            onClose(false);
          }
        }}
        scroll={"paper"}
        fullWidth
        maxWidth="md"
        classes={{ paper: classes.paper }}
      >
        <DialogTitle id="scroll-dialog-title" onClose={() => onClose(false)}>
          {title}
        </DialogTitle>
        <DialogContent dividers className={classes.content}>
          <div className={classes.root}>
        <div className={classes.currentCard}>
              <Typography variant="caption" style={{ opacity: 0.9 }}>
                {translate("ra.field.Move Group Info", {
                  code: currentNode?.AccGroupCode ?? currentNode?.GroupCode,
                  name: currentNode?.AccGroupName ?? currentNode?.GroupName,
                })}
              </Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 700, marginTop: 6 }}>
                {currentNode?.AccGroupCode ?? currentNode?.GroupCode} - {currentNode?.AccGroupName ?? currentNode?.GroupName}
              </Typography>
            </div>
            <Divider style={{ margin: "8px 0 16px" }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {moveDisabled ? (
                  <Typography variant="body2" color="textSecondary">
                    L1 cannot be moved.
                  </Typography>
                ) : null}
                {!moveDisabled && validParents.map((option) => {
                  const selected = (option.Id ?? "") === selectedParentId;
                  const indent = option._depth ? option._depth * 12 : 0;
                  return (
                    <button
                      type="button"
                      key={option.Id ?? "root"}
                      className={`${classes.optionCard} ${selected ? classes.optionSelected : ""}`}
                      onClick={() => setSelectedParentId(option.Id ?? "")}
                      aria-pressed={selected}
                      style={{
                        marginLeft: indent,
                        width: `calc(100% - ${indent}px)`,
                      }}
                    >
                      <div className={classes.optionBody}>
                        <div className={classes.optionCode}>{option.AccGroupCode ?? option.GroupCode}</div>
                        <div className={classes.optionName}>{option.AccGroupName ?? option.GroupName}</div>
                      </div>
                      <Chip size="small" label={`L${option.Level}`} />
                    </button>
                  );
                })}
                {!moveDisabled && validParents.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No available parent group at this level.
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  {translate("ra.field.Move Group Warning")}
                </Typography>
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <ButtonFooter
            noBorder
            disabled={isBtnLoading || moveDisabled}
            SaveFnc={handleSave}
            CancelFnc={handleCancel}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DialogMoveGroup;
